import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, Heart, MapPin, BedDouble, Maximize, Trees, Calendar, Home as HomeIcon,
  CalendarCheck, Mail, Ruler, Layers, Building2, Euro, ShieldCheck, Wrench, ClipboardCheck, Landmark, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSeo } from "../../lib/useSeo";
import { Button } from "../../components/ui/button";
import { MatchPill } from "../../components/ListingCard";
import { BookingDialog } from "../../components/koop/BookingDialog";
import { LeadDialog } from "../../components/koop/LeadDialog";
import { MortgageWidget } from "../../components/koop/MortgageWidget";
import { SALE_STATUS_LABEL } from "../../components/koop/koopUtils";
import { PhotoLightbox } from "../../components/detail/PhotoLightbox";
import { PrivateSellerNotice } from "../../components/koop/PrivateSellerNotice";
import { ListingTrafficPanel } from "../../components/admin/ListingTrafficPanel";

const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-xl border border-border px-3 py-2.5">
    <Icon size={16} className="shrink-0 text-brandblue" />
    <div className="min-w-0"><div className="text-[11px] font-semibold uppercase text-slate-400">{label}</div><div className="truncate text-sm font-semibold text-navy">{value}</div></div>
  </div>
);

const SERVICE_LEADS = [
  { key: "buying_agent", type: "buying_agent", icon: ShieldCheck, title: "Hulp nodig bij het kopen?", cta: "Aankoopmakelaar", subtitle: "Laat je begeleiden bij zoeken, bezichtigen, bieden en onderhandelen.",
    fields: [
      { name: "cities", label: "Gewenste plaats(en)", type: "text" },
      { name: "budget_range", label: "Budgetrange", type: "text", placeholder: "bijv. €350k–€450k" },
      { name: "property_type", label: "Woningtype", type: "text" },
      { name: "timeline", label: "Gewenste aankooptermijn", type: "select", options: [{ value: "0-3", label: "0–3 maanden" }, { value: "3-6", label: "3–6 maanden" }, { value: "6-12", label: "6–12 maanden" }, { value: "orienterend", label: "Oriënterend" }] },
      { name: "mortgage_ready", label: "Hypotheekindicatie?", type: "yesno" },
      { name: "help_with", label: "Hulp bij", type: "select", options: [{ value: "zoeken", label: "Zoeken" }, { value: "bezichtigen", label: "Bezichtigen" }, { value: "bieden", label: "Bieden" }, { value: "onderhandelen", label: "Onderhandelen" }, { value: "volledig", label: "Volledige begeleiding" }] },
    ] },
  { key: "mortgage", type: "mortgage", icon: Euro, title: "Wat kun je ongeveer lenen?", cta: "Hypotheekgesprek", subtitle: "We verbinden je met een onafhankelijk hypotheekadviseur. Geen productadvies via WoningAlert.",
    fields: [
      { name: "budget", label: "Koopprijs / budget (€)", type: "number" },
      { name: "starter", label: "Ben je starter?", type: "yesno" },
      { name: "own_home_to_sell", label: "Eigen woning te verkopen?", type: "yesno" },
      { name: "income_range", label: "Globale inkomensrange (optioneel)", type: "text" },
      { name: "timeline", label: "Wanneer wil je kopen?", type: "select", options: [{ value: "0-3", label: "0–3 maanden" }, { value: "3-6", label: "3–6 maanden" }, { value: "6-12", label: "6–12 maanden" }, { value: "orienterend", label: "Oriënterend" }] },
    ] },
  { key: "appraisal", type: "appraisal", icon: Landmark, title: "Taxatie nodig?", cta: "Taxatie aanvragen", subtitle: "Een erkend taxateur bepaalt de waarde van de woning.",
    fields: [
      { name: "reason", label: "Reden", type: "select", options: [{ value: "aankoop", label: "Aankoop" }, { value: "hypotheek", label: "Hypotheek" }, { value: "oversluiten", label: "Oversluiten" }, { value: "verbouwing", label: "Verbouwing" }, { value: "anders", label: "Anders" }] },
      { name: "purchase_signed", label: "Koopovereenkomst getekend?", type: "yesno" },
      { name: "preferred_date", label: "Gewenste datum", type: "text", placeholder: "bijv. volgende week" },
      { name: "deadline", label: "Deadline (indien aanwezig)", type: "text" },
    ] },
  { key: "building_inspection", type: "building_inspection", icon: Wrench, title: "Bouwkundige keuring plannen", cta: "Keuring plannen", subtitle: "Onafhankelijke bouwkundige keuring vóór aankoop.",
    fields: [
      { name: "year_built", label: "Bouwjaar (indien bekend)", type: "number" },
      { name: "purchase_price", label: "Koopprijs (€)", type: "number" },
      { name: "offer_accepted", label: "Bod geaccepteerd?", type: "yesno" },
      { name: "focus", label: "Specifieke aandacht", type: "select", options: [{ value: "dak", label: "Dak" }, { value: "fundering", label: "Fundering" }, { value: "vocht", label: "Vocht" }, { value: "scheuren", label: "Scheuren" }, { value: "installaties", label: "Installaties" }, { value: "geen", label: "Geen specifieke zorgen" }] },
      { name: "preferred_date", label: "Gewenste datum", type: "text" },
    ] },
  { key: "rental_inspection", type: "rental_inspection", icon: ClipboardCheck, title: "Woninginspectie", cta: "Inspectie plannen", subtitle: "Begin-, eind-, tussentijdse of opleverinspectie.",
    fields: [
      { name: "inspection_type", label: "Type inspectie", type: "select", options: [{ value: "begin", label: "Begininspectie" }, { value: "eind", label: "Eindinspectie" }, { value: "tussentijds", label: "Tussentijds" }, { value: "oplever", label: "Opleverinspectie" }] },
      { name: "role", label: "Ik ben", type: "select", options: [{ value: "huurder", label: "Huurder" }, { value: "verhuurder", label: "Verhuurder" }, { value: "beheerder", label: "Beheerder" }] },
      { name: "key_handover", label: "Datum sleuteloverdracht", type: "text" },
      { name: "furnished", label: "Gemeubileerd?", type: "yesno" },
      { name: "preferred_date", label: "Gewenste datum/tijd", type: "text" },
    ] },
];

const KoopDetail = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [l, setL] = useState(null);
  const [error, setError] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [fav, setFav] = useState(false);
  const [booking, setBooking] = useState(false);
  const [leadCfg, setLeadCfg] = useState(null); // {type,title,subtitle,fields}

  useSeo(l ? {
    exactTitle: `${l.title || l.property_type || "Woning"} te koop${l.city ? ` in ${l.city}` : ""}${l.price_label ? ` — ${l.price_label}` : ""} · WoningAlert`,
    description: `${l.property_type || "Woning"} te koop${l.city ? ` in ${l.city}` : ""}${l.m2 ? `: ${l.m2} m²` : ""}${l.bedrooms ? `, ${l.bedrooms} slaapkamers` : ""}${l.price_label ? `. ${l.price_label}.` : ""}`,
    image: l.image || (l.gallery && l.gallery[0]) || "/og-image.png",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": l.title || `${l.property_type || "Woning"} in ${l.city || ""}`,
        "description": (l.description || "").slice(0, 500) || undefined,
        "image": l.gallery?.length ? l.gallery.slice(0, 6) : (l.image ? [l.image] : undefined),
        "offers": l.price ? {
          "@type": "Offer",
          "priceCurrency": "EUR",
          "price": l.price,
          "availability": l.status === "verkocht"
            ? "https://schema.org/SoldOut"
            : "https://schema.org/InStock",
        } : undefined,
        "additionalType": "https://schema.org/Residence",
        "category": "RealEstate",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Koopwoningen", "item": `${window.location.origin}/koopwoningen` },
          l.city ? { "@type": "ListItem", "position": 2, "name": l.city, "item": `${window.location.origin}/koopwoningen/${encodeURIComponent(l.city.toLowerCase())}` } : null,
          { "@type": "ListItem", "position": l.city ? 3 : 2, "name": l.title || l.street || "Woning", "item": window.location.href },
        ].filter(Boolean),
      },
    ],
  } : { title: "Woning laden… · WoningAlert" });

  useEffect(() => {
    setL(null); setError(false);
    api(`/koop/by-slug/${slug}`).then((d) => { setL(d); setFav(!!d.is_favorite); }).catch(() => setError(true));
  }, [slug]);

  const toggleFav = async () => {
    if (!user) return toast.error("Log in om woningen op te slaan.");
    try {
      if (fav) { await api(`/koop/favorites/${l.id}`, { method: "DELETE" }); setFav(false); }
      else { await api("/koop/favorites", { method: "POST", body: JSON.stringify({ listing_id: l.id }) }); setFav(true); }
    } catch (e) { toast.error(e.message); }
  };

  if (error) return <div className="mx-auto max-w-md px-4 py-20 text-center"><p className="text-slate-500">Koopwoning niet gevonden.</p><Link to="/koopwoningen" className="mt-4 inline-block text-brandblue underline">Naar koopaanbod</Link></div>;
  if (!l) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" size={32} /></div>;

  const gallery = (l.gallery && l.gallery.length ? l.gallery : (l.image ? [l.image] : []));
  const mapSrc = l.lat && l.lng ? `https://www.openstreetmap.org/export/embed.html?bbox=${l.lng - 0.01}%2C${l.lat - 0.006}%2C${l.lng + 0.01}%2C${l.lat + 0.006}&layer=mapnik&marker=${l.lat}%2C${l.lng}` : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <Link to="/koopwoningen" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-navy">← Terug naar koopaanbod</Link>

      {/* Gallery */}
      <div className="overflow-hidden rounded-3xl border border-border bg-slate-100">
        <div className="relative aspect-[16/9] w-full bg-slate-200">
          {gallery[activeImg] ? (
            <button
              type="button"
              onClick={() => setLightboxIdx(activeImg)}
              data-testid="koop-gallery-main"
              className="h-full w-full cursor-zoom-in"
              aria-label="Open foto op volledig scherm"
            >
              <img src={gallery[activeImg]} alt={l.title} className="h-full w-full object-cover" />
            </button>
          ) : <div className="flex h-full items-center justify-center text-slate-300"><HomeIcon size={56} /></div>}
          {l.status && l.status !== "actief" && <span className="absolute left-4 top-4 rounded-full bg-navy px-3 py-1 text-xs font-bold text-white">{SALE_STATUS_LABEL[l.status]}</span>}
          {gallery.length > 0 && (
            <button
              type="button"
              onClick={() => setLightboxIdx(activeImg)}
              data-testid="koop-gallery-expand"
              className="absolute bottom-4 right-4 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-navy shadow-md transition hover:bg-white"
            >
              Alle foto&apos;s ({gallery.length})
            </button>
          )}
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 overflow-x-auto p-3">
            {gallery.slice(0, 12).map((g, i) => (
              <button key={i} onClick={() => setActiveImg(i)} onDoubleClick={() => setLightboxIdx(i)} className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 ${i === activeImg ? "border-brandblue" : "border-transparent"}`}>
                <img src={g} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <PhotoLightbox
        gallery={gallery}
        index={lightboxIdx}
        title={l.title}
        onClose={() => setLightboxIdx(null)}
        onChange={setLightboxIdx}
      />

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl font-extrabold text-navy">{l.title || `${l.property_type} in ${l.city}`}</h1>
              <p className="mt-1 flex items-center gap-1 text-slate-500"><MapPin size={15} /> {l.street} {l.house_number}, {l.postcode} {l.city}</p>
              <PrivateSellerNotice listing={l} className="mt-2" />
            </div>
            {typeof l.match_score === "number" && <MatchPill score={l.match_score} />}
          </div>

          {/* Maandlasten widget + bewaar-knop direct onder titel/locatie */}
          <div className="mt-4 flex items-stretch gap-3">
            <div className="min-w-0 flex-1">
              <MortgageWidget listing={l} />
            </div>
            <button
              data-testid="koop-fav-toggle"
              onClick={toggleFav}
              aria-label={fav ? "Woning verwijderen uit bewaard" : "Woning bewaren"}
              className="group flex w-14 shrink-0 items-center justify-center self-stretch rounded-2xl border border-brandblue/20 bg-white transition-colors hover:border-danger hover:bg-danger/5 sm:w-16"
            >
              <Heart size={22} className={fav ? "fill-danger text-danger" : "text-slate-400 group-hover:text-danger"} />
            </button>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Fact icon={Maximize} label="Woonopp." value={`${l.m2} m²`} />
            {l.plot_m2 ? <Fact icon={Trees} label="Perceel" value={`${l.plot_m2} m²`} /> : null}
            {l.volume_m3 ? <Fact icon={Layers} label="Inhoud" value={`${l.volume_m3} m³`} /> : null}
            <Fact icon={BedDouble} label="Slaapkamers" value={l.bedrooms} />
            {l.rooms ? <Fact icon={Building2} label="Kamers" value={l.rooms} /> : null}
            {l.year_built ? <Fact icon={Calendar} label="Bouwjaar" value={l.year_built} /> : null}
            {l.energy_label && l.energy_label !== "—" ? <Fact icon={Ruler} label="Energielabel" value={l.energy_label} /> : null}
            <Fact icon={HomeIcon} label="Type" value={l.property_type} />
          </div>

          {l.description && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-navy">Omschrijving</h2>
              <p className="mt-3 whitespace-pre-line text-slate-600">{l.description}</p>
            </div>
          )}

          {/* Hulp rondom je aankoop — direct na beschrijving */}
          <div className="mt-8">
            <h2 className="font-display text-xl font-bold text-navy">Hulp rondom je aankoop</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {SERVICE_LEADS.map((s) => (
                <button key={s.key} data-testid={`service-${s.key}`} onClick={() => setLeadCfg({ type: s.type, title: s.title, subtitle: s.subtitle, fields: s.fields })}
                  className="flex items-start gap-3 rounded-2xl border border-border p-4 text-left transition-colors hover:border-brandblue/50">
                  <s.icon size={20} className="mt-0.5 shrink-0 text-brandblue" />
                  <div><div className="text-sm font-bold text-navy">{s.cta}</div><div className="text-xs text-slate-500">{s.title}</div></div>
                </button>
              ))}
            </div>
          </div>

          {l.features && l.features.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-navy">Kenmerken</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {l.features.map((ft, i) => <span key={i} className="rounded-full bg-slate-50 px-3 py-1.5 text-sm text-slate-600">{ft}</span>)}
              </div>
            </div>
          )}

          {(l.ownership || l.vve_contribution || l.service_costs || l.monument) && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-navy">Juridisch & financieel</h2>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {l.ownership ? <Fact icon={ShieldCheck} label="Eigendom" value={l.ownership} /> : null}
                {l.vve_contribution ? <Fact icon={Euro} label="VvE p/m" value={`€ ${l.vve_contribution}`} /> : null}
                {l.service_costs ? <Fact icon={Euro} label="Servicekosten" value={`€ ${l.service_costs}`} /> : null}
                {l.monument ? <Fact icon={Landmark} label="Monument" value="Ja" /> : null}
              </div>
            </div>
          )}

          {mapSrc && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-navy">Locatie</h2>
              <div className="mt-3 overflow-hidden rounded-2xl border border-border"><iframe title="kaart" src={mapSrc} className="h-64 w-full" loading="lazy" /></div>
            </div>
          )}

          {/* Seller / valuation CTA — P0 monetisation, prominent but tasteful */}
          <div className="mt-10 rounded-3xl border border-brandblue/30 bg-brandblue-light/40 p-6" data-testid="seller-cta">
            <h3 className="font-display text-lg font-bold text-navy">Heb je zelf een woning te verkopen?</h3>
            <p className="mt-1 text-sm text-slate-600">Benieuwd wat jouw huidige woning waard is? Vraag een gratis, vrijblijvende waardebepaling aan.</p>
            <Button
              data-testid="valuation-cta"
              onClick={() => setLeadCfg({ type: "valuation", title: "Gratis waardebepaling", subtitle: "Vul je gegevens in — een makelaar neemt contact op.",
                fields: [
                  { name: "postcode", label: "Postcode", type: "text" },
                  { name: "house_number", label: "Huisnummer + toevoeging", type: "text" },
                  { name: "property_type", label: "Woningtype (optioneel)", type: "text" },
                  { name: "m2_estimate", label: "Geschatte woonopp. (optioneel)", type: "number" },
                  { name: "timeline", label: "Wanneer mogelijk verkopen?", type: "select", options: [{ value: "0-3", label: "0–3 maanden" }, { value: "3-6", label: "3–6 maanden" }, { value: "6-12", label: "6–12 maanden" }, { value: "orienterend", label: "Oriënterend" }] },
                  { name: "has_agent", label: "Al een makelaar?", type: "yesno" },
                  { name: "appointment_pref", label: "Gewenste afspraak", type: "select", options: [{ value: "telefonisch", label: "Telefonisch" }, { value: "thuis", label: "Bij mij thuis" }, { value: "onzeker", label: "Nog niet zeker" }] },
                ] })}
              className="mt-4 rounded-xl bg-brandblue hover:bg-brandblue/90"
            >
              Gratis waardebepaling
            </Button>
          </div>
        </div>

        {/* Sticky action rail */}
        <aside>
          <div className="sticky top-24 space-y-4 rounded-3xl border border-border bg-white p-6 shadow-sm">
            <div className="font-display text-3xl font-extrabold text-navy">{l.price_label}</div>
            <div className="text-sm text-slate-400">{l.agency_name}</div>
            <Button data-testid="plan-viewing-cta" onClick={() => setBooking(true)} disabled={l.status === "verkocht"} className="w-full rounded-xl bg-brandblue py-6 text-base hover:bg-brandblue/90">
              <CalendarCheck size={18} className="mr-2" /> Plan een bezichtiging
            </Button>
            <Button data-testid="interest-cta" variant="outline" onClick={() => setLeadCfg({ type: "purchase_interest", title: "Ik heb interesse", subtitle: "Laat je gegevens achter — de makelaar neemt contact op.", fields: [{ name: "message", label: "Bericht (optioneel)", type: "textarea" }] })}
              className="w-full rounded-xl py-6 text-base">
              <Mail size={18} className="mr-2" /> Ik heb interesse
            </Button>
            {user?.role === "admin" && typeof l.view_count === "number" && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-center gap-1.5 rounded-xl bg-slate-50 py-2 text-xs text-slate-500" data-testid="koop-view-count-admin">
                  <Eye size={13} /> Admin: {l.view_count === 1 ? "1 keer" : `${l.view_count} keer`} bekeken
                </div>
                <ListingTrafficPanel lid={l.id} />
              </div>
            )}
          </div>
        </aside>
      </div>

      <BookingDialog open={booking} onOpenChange={setBooking} listingId={l.id} />
      {leadCfg && (
        <LeadDialog
          open={!!leadCfg}
          onOpenChange={(v) => !v && setLeadCfg(null)}
          leadType={leadCfg.type}
          propertyId={l.id}
          title={leadCfg.title}
          subtitle={leadCfg.subtitle}
          qualificationFields={leadCfg.fields}
          ctaLabel="Versturen"
        />
      )}
    </div>
  );
};

export default KoopDetail;
